import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { StorageUsageDto } from '@gr/core/platform/admin/dto';
import { useServices, useT } from '../../../app/di';
import { formatDateTime } from '../../../shared/format';
import { useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { PlatformOverview } from './PlatformOverview/PlatformOverview';

// ADM-UC-1, ADM-RULE-1, ADM-UC-1.CC-1
export function AdminOverviewPage(): ReactElement {
  const { api } = useServices();
  const { t, te, locale } = useT();
  const overview = useResource(() => api.admin.overview(), [api]);
  const [storage, setStorage] = useState<{ usage: StorageUsageDto | null; loading: boolean; error: string[] | null }>({ usage: null, loading: false, error: null });

  // Замер хранилища — отдельным запросом после обзора: на S3 он долгий (ADM-UC-1.CC-1).
  const measure = useCallback(async () => {
    setStorage((current) => ({ ...current, loading: true, error: null }));
    const result = await api.admin.storageUsage();
    setStorage({ usage: result.success ? result.data : null, loading: false, error: result.success ? null : result.messages });
  }, [api]);

  useEffect(() => {
    if (overview.data) void measure();
  }, [overview.data !== null, measure]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={t('admin.overview.title')}
        subtitle={
          <>
            {t('admin.overview.subtitle')}
            {overview.data ? <span className="u-muted u-small"> {t('admin.overview.generatedAt', { time: formatDateTime(overview.data.generatedAt, locale) })}</span> : null}
          </>
        }
        actions={
          <Button icon="search" loading={overview.loading} onClick={() => void overview.reload()}>
            {t('admin.overview.refresh')}
          </Button>
        }
      />
      {overview.loading && !overview.data ? (
        <Loading label={t('common.loading')} />
      ) : overview.error || !overview.data ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          {te(overview.error)}
        </Alert>
      ) : (
        <PlatformOverview overview={overview.data} storage={{ ...storage, onMeasure: () => void measure() }} />
      )}
    </div>
  );
}
